export class ScrollMenu {
  constructor(menuElement) {
    if (!(menuElement instanceof Element)) {
      throw new Error('ScrollMenu constructor requires a DOM element');
    }
    this.menu = menuElement;
    this.init();
  }

  init() {
    this.menuItems = [...this.menu.querySelectorAll('a')];
    this.sections = this.menuItems
      .map(item => {
        const href = item.getAttribute('href');
        if (href) {
          const id = href.split('#')[1];
          return id ? document.getElementById(id) : null;
        }
        return null;
      })
      .filter(Boolean);

    this.currentActive = null;
    this.setupIntersectionObserver();
    this.bindEvents();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: `${this.getScrollPadding()}px 0px 0px 0px`,
      threshold: [0, 0.1, 0.5, 1]
    };

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), options);
    this.sections.forEach(section => this.observer.observe(section));
  }

  handleIntersection(entries) {
    this.updateActiveSection();
  }

  updateActiveSection() {
    const scrollPosition = window.scrollY + this.getScrollPadding();
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    const isNearBottom = (scrollPosition + windowHeight) >= documentHeight - 50;

    if (isNearBottom) {
      this.setActiveSection(this.sections[this.sections.length - 1]);
      return;
    }

    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];
      const sectionTop = section.offsetTop;
      const sectionBottom = sectionTop + section.offsetHeight;

      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        this.setActiveSection(section);
        return;
      }
    }

    let closestSection = this.sections[0];
    let closestDistance = Math.abs(scrollPosition - closestSection.offsetTop);

    for (let i = 1; i < this.sections.length; i++) {
      const section = this.sections[i];
      const distance = Math.abs(scrollPosition - section.offsetTop);
      if (distance < closestDistance) {
        closestSection = section;
        closestDistance = distance;
      }
    }

    this.setActiveSection(closestSection);
  }

  setActiveSection(section) {
    if (section !== this.currentActive) {
      this.setAriaCurrentMenuItem(section);
      this.currentActive = section;
    }
  }

  bindEvents() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.menuItems.forEach(item => {
      item.addEventListener('click', this.onMenuItemClick);
    });
  }

  onScroll = () => {
    requestAnimationFrame(() => this.updateActiveSection());
  }

  onMenuItemClick = (event) => {
    const href = event.currentTarget.getAttribute('href');
    if (href) {
      const targetId = href.split('#')[1];
      const targetSection = document.getElementById(targetId);
      if (targetSection) {
        event.preventDefault();
        this.scrollToSection(targetSection);
        this.setActiveSection(targetSection);
        this.updateURL(targetSection);
      }
    }
  }

  scrollToSection(section) {
    const targetPosition = section.getBoundingClientRect().top + window.pageYOffset - this.getScrollPadding();
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });

    // Focus on the section for accessibility
    section.setAttribute('tabindex', '-1');
    section.focus({ preventScroll: true });
  }

  setAriaCurrentMenuItem(activeSection) {
    this.menuItems.forEach(item => {
      const href = item.getAttribute('href');
      const id = href ? href.split('#')[1] : null;
      if (id && document.getElementById(id) === activeSection) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  updateURL(section) {
    if (section && section.id) {
      history.pushState(null, '', `#${section.id}`);
    }
  }

  getScrollPadding() {
    return parseInt(getComputedStyle(document.documentElement).scrollPaddingTop, 10) || 0;
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('scroll', this.onScroll);
    this.menuItems.forEach(item => {
      item.removeEventListener('click', this.onMenuItemClick);
    });
  }
}
